import { IconFa } from '@/components/wm';
import { cn } from '@/utils';
import React, { useEffect, useState } from 'react';

import { DEFAULT_LEVEL_GRAPHIC, formatPercent, LevelGraphic } from './LevelGraphic';
import { useFittedHeadline } from './useValueFontSize';

const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

// Not clamped, so an overfill or a faulty sensor is not hidden.
const fillShare = (value, min, max) => (value - min) / (max - min);

// `sample`, when given, stands in for the indicator and its reading: the type
// picker shows each widget with made-up values.
export default function LevelWidget({ sample, widget }) {
    const
        [fetched, setReading] = useState(null),
        reading = sample?.reading ?? fetched;

    const
        model = WiseMetering.indicators.get(widget.data.indicatorId),
        palette = widget.graphical.palette || 'primary',
        { max, min } = widget.data,
        isSmallWidget = widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2;

    const
        decimals = widget.data.decimals,
        icon = WiseMetering.icons.getIconFromKind(model?.get('kind_slug')) || 'info',
        unit = sample?.unit ?? (model?.get('unit') || ''),
        timezone = model?.timezone() || moment.tz.guess(),
        unitScale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined,
        format = value => formatValue(value, unit, decimals, unitScale);

    useEffect(() => {
        if (sample || !model) {
            return;
        }

        let cancelled = false;

        setReading(null);

        const fetchData = () => {
            model.fetchIndicatorData('/last_value', {}).then(response => {
                if (!cancelled) {
                    setReading(response?.v === null || response?.v === undefined ? { empty: true } : response);
                }
            }).catch(() => {
                if (!cancelled) {
                    setReading({ empty: true });
                }
            });
        };

        fetchData();

        const refresh = setInterval(fetchData, 60000);

        return () => {
            cancelled = true;
            clearInterval(refresh);
        };
    }, [model]);

    // Not ordered: a sensor that reads the distance down to the surface has its
    // full reading below its empty one.
    const
        hasRange = Number.isFinite(min) && Number.isFinite(max) && max !== min,
        share = hasRange && reading?.v !== undefined ? fillShare(reading.v, min, max) : null,
        percentage = share === null ? '' : formatPercent(share * 100),
        headline = share === null ? '' : format(reading.v),
        [valueRef, fontSize] = useFittedHeadline(headline);

    let body;

    if ((!model && !sample) || !hasRange || reading?.empty) {
        body = <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm">{i18next.t('ui.no_data_available')}</div>;
    } else if (!reading) {
        body = <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm">{i18next.t('modal.title.loading')}</div>;
    } else {
        body = (
            <div className="tw-flex tw-h-full tw-items-stretch tw-gap-4 tw-pb-2">
                {/* Half the width whatever the graphic, so the value is fitted to
                    the same room, and drawn at the same size, on all of them. */}
                <div className="tw-flex tw-h-full tw-w-1/2 tw-flex-shrink-0">
                    <LevelGraphic
                        className="tw-h-full tw-w-auto"
                        fitWidth
                        graphic={widget.graphical.graphic || DEFAULT_LEVEL_GRAPHIC}
                        levelLabel={percentage}
                        palette={palette}
                        share={share}
                    />
                </div>
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-end tw-justify-end" ref={valueRef}>
                    <div className="tw-max-w-full tw-truncate tw-font-bold tw-text-right tw-leading-tight" style={{ fontSize: `${fontSize}px` }}>
                        {headline}
                    </div>
                    <div className={cn('tw-font-medium tw-text-right tw-mt-0.5 tw-max-w-full tw-truncate', isSmallWidget ? 'tw-text-sm' : 'tw-text-base')}>
                        {i18next.t('glossary.last_value').capitalize()}
                    </div>
                    <div className="tw-text-[11px] tw-text-right tw-opacity-70 tw-mt-[-4px]">
                        {moment.unix(reading.t).tz(timezone).format('YYYY/MM/DD HH:mm')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-h-full tw-w-full tw-overflow-hidden'
            )}
            id={widget.id}
        >
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-base tw-font-medium tw-leading-none">{widget.title}</div>
                    <IconFa color={iconVariants[palette]} icon={icon} size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            <div className="tw-flex-1 tw-min-h-0">
                {body}
            </div>
        </div>
    );
}
